// 모바일 차수별현황
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAccounts }       from '../../api/accounts';
import { fetchTurnStatus }     from '../../api/profit';
import { fetchRealtimePrices } from '../../api/price';
import AccountChips from '../components/AccountChips';
import '../styles/mobile.css';

// ── 포맷 헬퍼 ─────────────────────────────────────────────────
const fRate = v => {
  if (v == null) return '';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
};
const fPrice = v => {
  if (v == null) return '';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toLocaleString('ko-KR');
};
const fQty = v => {
  if (v == null) return '';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { maximumFractionDigits: 4 })
    : n.toLocaleString('ko-KR');
};
const fDate = d => {
  if (!d) return '';
  return `${d.substring(2, 4)}.${d.substring(5, 7)}.${d.substring(8, 10)}`;
};
const clsCls = v => (Number(v) > 0 ? 'pos' : Number(v) < 0 ? 'neg' : '');

// ── 미매도 차수 총 수량 ────────────────────────────────────────
const getTotalQty = row => {
  const unsold = Object.values(row.turns || {}).filter(t => t.type === 'unsold');
  return unsold.length ? unsold.reduce((s, t) => s + (t.quantity || 0), 0) : null;
};

// ── 실시간 시세 적용 ───────────────────────────────────────────
const applyPrices = (rows, priceRes) => {
  const map = {};
  for (const p of (priceRes.data || [])) map[p.ticker] = p;
  return rows.map(row => {
    const p = map[row.ticker];
    if (!p?.current_price) return row;
    const curr  = p.current_price;
    const turns = {};
    for (const [k, t] of Object.entries(row.turns || {})) {
      turns[k] = (t.type === 'unsold' && t.buy_price != null)
        ? { ...t, profit_rate: Math.round((curr - t.buy_price) / t.buy_price * 10000) / 100 }
        : t;
    }
    return { ...row, current_price: curr, diff_rate: p.diff_rate, turns };
  });
};

// ── 모드별 차수 셀 값 ──────────────────────────────────────────
const cellContent = (turn, mode) => {
  if (!turn || turn.type !== 'unsold') return null;
  if (mode === 'rate')  return turn.profit_rate != null ? fRate(turn.profit_rate)  : '';
  if (mode === 'date')  return fDate(turn.buy_date);
  if (mode === 'qty')   return turn.quantity  != null  ? fQty(turn.quantity)       : '';
  if (mode === 'price') return turn.buy_price != null  ? fPrice(turn.buy_price)    : '';
  return '';
};

const MODES = [
  { value: 'rate',  label: '수익률' },
  { value: 'date',  label: '날짜' },
  { value: 'qty',   label: '수량' },
  { value: 'price', label: '매입단가' },
];

// ════════════════════════════════════════════════════════════
// 메인 컴포넌트
// ════════════════════════════════════════════════════════════
export default function TurnStatusPage() {
  const navigate = useNavigate();
  const [allAccounts,   setAllAccounts]   = useState([]);

  // 계좌 선택 — 페이지 이동 후 복귀해도 유지
  const [selectedAccts, setSelectedAccts] = useState(() => {
    try {
      const s = sessionStorage.getItem('mb_turn_selected_accts');
      return s ? JSON.parse(s) : [];
    } catch { return []; }
  });

  const [displayMode, setDisplayMode] = useState('rate');
  const [rawData,     setRawData]     = useState([]);
  const [maxTurnNo,   setMaxTurnNo]   = useState(0);
  const [loading,     setLoading]     = useState(false);

  useEffect(() => {
    sessionStorage.setItem('mb_turn_selected_accts', JSON.stringify(selectedAccts));
  }, [selectedAccts]);

  // ── 계좌 목록 ─────────────────────────────────────────────
  useEffect(() => {
    fetchAccounts()
      .then(d => setAllAccounts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // ── 데이터 로드 (DB 재집계 + 실시간 시세) ────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetchTurnStatus({ include_sold: false });
      const rows = res.data ?? [];
      setMaxTurnNo(res.max_turn_no ?? 0);

      const tickers = [...new Set(rows.map(r => r.ticker).filter(Boolean))];
      let final = rows;
      if (tickers.length) {
        try {
          const priceRes = await fetchRealtimePrices(tickers);
          final = applyPrices(rows, priceRes);
        } catch {}
      }
      setRawData(final);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── 종목 행 탭 → 시세 그래프 이동 ──────────────────────
  const goToChart = row => {
    const turns = Object.entries(row.turns || {})
      .filter(([, t]) => t.type === 'unsold')
      .map(([n, t]) => ({
        turn_no:     Number(n),
        buy_date:    t.buy_date,
        buy_price:   t.buy_price,
        quantity:    t.quantity,
        profit_rate: t.profit_rate,
      }))
      .sort((a, b) => a.turn_no - b.turn_no);

    navigate('/mobile/stock-chart', {
      state: {
        ticker:        row.ticker,
        stock_name:    row.stock_name || row.ticker,
        account_name:  row.account_name,
        term_type:     row.term_type,
        current_price: row.current_price,
        diff_rate:     row.diff_rate,
        turns,
      },
    });
  };

  // ── 계좌 필터 (클라이언트 사이드, API 재호출 없음) ───────
  const displayData = useMemo(() =>
    selectedAccts.length === 0
      ? rawData
      : rawData.filter(r => selectedAccts.includes(r.account_name)),
    [rawData, selectedAccts]
  );

  // ── 차수 컬럼: 항상 1~5 기본, 6차 이상은 max_turn_no까지
  const turnCols = useMemo(() => {
    const cols = [1, 2, 3, 4, 5];
    for (let n = 6; n <= maxTurnNo; n++) cols.push(n);
    return cols;
  }, [maxTurnNo]);

  // ── 렌더 ──────────────────────────────────────────────────
  return (
    <>
      {/* 계좌 칩 */}
      <AccountChips
        accounts={allAccounts}
        selected={selectedAccts}
        onSelect={setSelectedAccts}
      />

      {/* 표시 모드 칩 + 재집계 버튼 */}
      <div className="ts-controls">
        <div className="ts-mode-chips">
          {MODES.map(({ value, label }) => (
            <button
              key={value}
              className={`mb-chip${displayMode === value ? ' active' : ''}`}
              onClick={() => setDisplayMode(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          className={`mb-refresh-btn${loading ? ' spinning' : ''}`}
          onClick={loadData}
          disabled={loading}
          title="재집계"
        >↻</button>
      </div>

      {/* 테이블 */}
      <div className="ts-table-wrap">
        {loading ? (
          <div className="mb-loading">불러오는 중...</div>
        ) : displayData.length === 0 ? (
          <div className="mb-empty">데이터가 없습니다.</div>
        ) : (
          <table className="ts-table">
            <thead>
              <tr>
                <th className="ts-th ts-th-name">종목 / 계좌</th>
                <th className="ts-th ts-th-r">수량</th>
                <th className="ts-th ts-th-r ts-th-sep">등락</th>
                {turnCols.map(n => (
                  <th key={n} className="ts-th ts-th-turn">{n}차</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, idx) => {
                const totalQty = getTotalQty(row);
                return (
                  <tr
                    key={idx}
                    className={`ts-row${idx % 2 === 1 ? ' ts-row-even' : ''} ts-row-tap`}
                    onClick={() => goToChart(row)}
                  >

                    {/* 종목명 / 계좌 (sticky) */}
                    <td className="ts-td ts-td-name">
                      <div className="ts-stock-name">{row.stock_name || row.ticker}</div>
                      <div className="ts-stock-sub">
                        {[row.account_name, row.term_type].filter(Boolean).join(' · ')}
                      </div>
                    </td>

                    {/* 총 수량 */}
                    <td className="ts-td ts-td-r">
                      {totalQty != null ? fQty(totalQty) : '—'}
                    </td>

                    {/* 등락률 */}
                    <td className={`ts-td ts-td-r ts-td-sep ${clsCls(row.diff_rate)}`}>
                      {row.diff_rate != null ? fRate(row.diff_rate) : '—'}
                    </td>

                    {/* 차수 셀 */}
                    {turnCols.map(n => {
                      const turn     = row.turns?.[String(n)];
                      const isUnsold = turn?.type === 'unsold';
                      const content  = cellContent(turn, displayMode);
                      const rate     = isUnsold ? (turn?.profit_rate ?? null) : null;

                      const bgStyle = isUnsold ? {
                        backgroundColor:
                          rate == null ? '#f5f5f5'
                          : rate >= 0  ? '#fff5f5'
                          :              '#f0f4ff',
                      } : {};

                      const txtCls = (isUnsold && displayMode === 'rate' && rate != null)
                        ? clsCls(rate) : '';

                      return (
                        <td
                          key={n}
                          className={`ts-td ts-td-turn ${txtCls}`}
                          style={bgStyle}
                          onClick={e => {
                            e.stopPropagation();
                            if (isUnsold) {
                              navigate('/mobile/quick-trade', {
                                state: {
                                  mode:         'sell',
                                  account_name: row.account_name,
                                  ticker:       row.ticker,
                                  stock_name:   row.stock_name || row.ticker,
                                  turn_no:      n,
                                },
                              });
                            } else {
                              navigate('/mobile/quick-trade', {
                                state: {
                                  mode:         'buy',
                                  account_name: row.account_name,
                                  ticker:       row.ticker,
                                  stock_name:   row.stock_name || row.ticker,
                                },
                              });
                            }
                          }}
                        >
                          {isUnsold ? (content || '') : '—'}
                        </td>
                      );
                    })}

                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

    </>
  );
}
